import { useRef, useEffect } from "react";
import * as d3 from "d3";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

const MosaicPlot = ({
    data,
    primaryCountName = "Minutes",
    secondaryCountName = "Contacts",
    width = 1850,
    height = 750,
    marginTop = 10,
    marginRight = 130,
    marginBottom = 10,
    marginLeft = 0,
    widthThreshold = 150,

    // If the row that is about to be added will make existing rows take up more than this percentage of remaining space in the column,
    // group that and all remaining rows into one "other" row.
    otherRowThreshold = 0.05,
    otherRowHeightThresHold = 0.023,

    // If the width for a column is less than this amount, hide the text that would go inside them.
    labelWidthThreshold = 30,

    // Columns get the column label vertically if the column is too small, but if they are way too small for that then this gets skipped.
    verticalColumnLabelThreshold = 10,

    rowName = "rowName",
    columnName = "columnName",
    primaryCount = "primaryCount",
    secondaryCount = "secondaryCount",

    // For downloading SVG
    svgRef = null,
}) => {
    const chartRef = useRef();

    useEffect(() => {
        if (data && chartRef.current) {
            const labelRowHeight = 65;
            const leftScaleWidthOffset = 40;

            const processedData = processData(
                data.chartData,
                otherRowThreshold,
                otherRowHeightThresHold,
                rowName,
                columnName,
                primaryCount,
                secondaryCount
            );
            console.log("MosaicPlot - Processed data:", processedData);

            const primaryCountTotal = processedData.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );

            const createChart = () => {
                const columnNameColors = d3
                    .scaleOrdinal()
                    .domain(Array.from(new Set(processedData.map((d) => d.columnName))))
                    .range(d3.schemeCategory10);

                const colorOverrides = data.columnColorOverrides || {};
                console.log("MosaicPlot - Color overrides received:", colorOverrides);

                // Create the color scale.
                const color = (d) => {
                    const columnName = d.parent.data[0];
                    //console.log("MosaicPlot - Looking for color for column:", columnName);

                    // Convert column name to title case to match the color config keys
                    const titleCaseColumnName = formatCellName(columnName);
                    //console.log("MosaicPlot - Title case column name:", titleCaseColumnName);

                    const baseColor = colorOverrides[titleCaseColumnName] || colorOverrides[columnName] || columnNameColors(columnName);
                    //console.log("MosaicPlot - Selected color:", baseColor);

                    const parentChildren = d.parent.children || [];
                    const childIndex = parentChildren.indexOf(d);
                    const totalChildren = parentChildren.length;

                    const rgb = d3.rgb(baseColor);

                    // Start the color scale on the lightest version of the chosen color.
                    const lightestTint = d3.rgb(
                        Math.min(255, rgb.r + (255 - rgb.r) * 0.95),
                        Math.min(255, rgb.g + (255 - rgb.g) * 0.95),
                        Math.min(255, rgb.b + (255 - rgb.b) * 0.95)
                    );

                    const colorScale = d3
                        .scaleLinear()
                        .domain([0, totalChildren - 1])
                        .range([lightestTint, baseColor]);

                    // Use the child's index to determine its color.
                    return colorScale(childIndex);
                };

                // Compute the layout.
                const treemap = (data) =>
                    d3
                        .treemap()
                        .round(true)
                        .tile(d3.treemapSliceDice)
                        .size([
                            // Account for 1 pixel border
                            width - marginLeft - marginRight - 1 + leftScaleWidthOffset,
                            height - marginTop - marginBottom - labelRowHeight - 1,
                        ])(
                            d3
                                .hierarchy(
                                    d3.group(
                                        data,
                                        (d) => d.columnName,
                                        (d) => d.rowName
                                    )
                                )
                                .sum((d) => d.primaryCount)
                                .eachAfter((d) => {
                                    // Add secondaryCount
                                    d.secondaryCount =
                                        d.data.secondaryCount ||
                                        d.children?.reduce(
                                            (sum, child) => sum + child.secondaryCount,
                                            0
                                        ) ||
                                        0;

                                    // Add "other" row info
                                    // d.data[1] and d.data[0] might be undefined.
                                    d.otherItems =
                                        d.data.otherItems ?? d.data[1]?.[0]?.otherItems;
                                })
                        )
                        .each((d) => {
                            // Account for 1 pixel border
                            d.x0 += marginLeft + 1 + leftScaleWidthOffset;
                            d.x1 += marginLeft + 1 + leftScaleWidthOffset;
                            d.y0 += marginTop + 1;
                            d.y1 += marginTop + 1;
                        });

                const root = treemap(processedData);

                const chart = d3.select(chartRef.current);

                // Clear any existing SVG
                chart.selectAll("*").remove();

                // Create the SVG container.
                const svg = chart
                    .append("svg")
                    .attr("width", width)
                    .attr("height", height)
                    .attr("style", "font: 10px sans-serif;");

                // Position the nodes.
                const node = svg
                    .selectAll("g")
                    .data(root.descendants())
                    .join("g")
                    .attr("transform", (d) => `translate(${d.x0},${d.y0})`);

                console.log("MosaicPlot - NODE:", node);

                const format = d3.format(",.5~r");
                const percentageFormat = d3.format(".1%");

                // Draw columnName labels.
                const columnName = node.filter((d) => d.depth === 1);

                columnName.each(function (d, i, nodes) {
                    const g = d3.select(this);

                    // Bring to top so the text is visible.
                    g.raise();

                    const width = d.x1 - d.x0;
                    const height = d.y1 - d.y0;

                    // Add a transparent rectangle for the entire label area
                    g.append("rect")
                        .attr("x", 0)
                        .attr("y", height)
                        .attr("width", (d) => d.x1 - d.x0)
                        .attr("height", labelRowHeight)
                        .attr("fill", "transparent")
                        .attr("stroke-width", "0px"); // No border

                    if (width > labelWidthThreshold) {
                        // Column labels
                        g.append("text")
                            .attr("x", width / 2)
                            .attr("y", height + 20)
                            .style("text-anchor", "middle")
                            .style("font-weight", "bold")
                            .style("font-size", "12px")
                            .text((d) => {
                                return formatCellName(d.data[0]);
                            })
                            .call(wrap, width, height, 5)
                            .attr("class", "cell-text");
                    } else if (width > verticalColumnLabelThreshold) {
                        const fontSize = 12;
                        g.append("text")
                            .attr("text-anchor", "middle")
                            .attr("dominant-baseline", "central")
                            .attr("font-size", fontSize + "px")
                            .attr("class", "cell-text vertical-text")
                            .text((d) => d.data[0])
                            .each(function () {
                                const text = d3.select(this);
                                const words = text.text().split("");
                                text.text("");

                                const letterHeight = fontSize * 1;
                                const offset = words.length * letterHeight;
                                const startY = height / 2 - offset / 2;

                                words.forEach((letter, i) => {
                                    text
                                        .append("tspan")
                                        .attr("x", width / 2)
                                        .attr("y", startY + i * letterHeight)
                                        .attr("dy", "0.8em") // Adjust this value if needed for fine-tuning vertical position
                                        .text(letter)
                                        .attr("class", "vertical-text");
                                });
                            });
                    }

                    if (width > widthThreshold) {
                        g.append("text")
                            .attr("x", width / 2)
                            .attr("y", height + 45)
                            .attr("fill-opacity", 0.7)
                            .style("text-anchor", "middle")
                            .text((d) => {
                                return `${primaryCountName}: ${format(d.value)} (${d3.format(
                  ".1%"
                )(d.value / primaryCountTotal)})`;
                            });
                        g.append("text")
                            .attr("x", width / 2)
                            .attr("y", height + 58)
                            .attr("fill-opacity", 0.7)
                            .style("text-anchor", "middle")
                            .text((d) => {
                                return `${secondaryCountName}: ${format(d.secondaryCount)}`;
                            });
                    }

                    g.append("line")
                        .attr("x1", -0.5)
                        .attr("x2", -0.5)
                        .attr("y1", 0)
                        .attr("y2", () => height + labelRowHeight)
                        .attr("stroke", "#000");

                    // horizontal line (top)
                    g.append("line")
                        .attr("x1", 0 - 1)
                        .attr("x2", width)
                        .attr("y1", 0)
                        .attr("y2", 0)
                        .attr("stroke", "#000");

                    // horizontal line (bottom)
                    g.append("line")
                        .attr("x1", 0)
                        .attr("x2", width)
                        .attr("y1", height + labelRowHeight)
                        .attr("y2", height + labelRowHeight)
                        .attr("stroke", "#000");

                    // Right vertical line only for the last columnName
                    if (i === nodes.length - 1) {
                        g.append("line")
                            .attr("x1", width - 0.5)
                            .attr("x2", width - 0.5)
                            .attr("y1", 0)
                            .attr("y2", height + labelRowHeight)
                            .attr("stroke", "#000");
                    }
                });

                // Find the total width of the chart
                const totalWidth = d3.max(node.data(), (d) => d.x1);

                const startY = marginTop + 5;
                const endY = height - 5 - labelRowHeight;

                const range = endY - startY;
                const step = range / 5;

                // Create percentages next to the chart.
                for (let i = 0; i <= 5; i++) {
                    const percentage = 100 - i * 20;
                    const value = Math.round(startY + step * i);

                    // Left
                    svg
                        .append("text")
                        .attr("x", 30)
                        .attr("y", value) // Adjust this value as needed
                        .style("text-anchor", "end")
                        .style("font-weight", "bold")
                        .style("font-size", "12px")
                        .text(percentage === 100 ? percentage + "%" : percentage);

                    // Right
                    svg
                        .append("text")
                        .attr("x", totalWidth + 10)
                        .attr("y", value) // Adjust this value as needed
                        .style("text-anchor", "start")
                        .style("font-weight", "bold")
                        .style("font-size", "12px")
                        .text(percentage === 100 ? percentage + "%" : percentage);
                }

                // Draw cells.
                const cell = node.filter((d) => d.depth === 2);

                cell
                    .append("rect")
                    .attr("fill", (d) => color(d))
                    .attr("fill-opacity", 0.75)
                    .attr("width", (d) => Math.max(0, d.x1 - d.x0 - 1)) // Fix negative width error
                    .attr("height", (d) => Math.max(0, d.y1 - d.y0)) // Ensure height is never negative either
                    .attr("stroke-dasharray", (d) => `${d.x1 - d.x0} ${d.y1 - d.y0}`); // No stroke on sides

                cell.each(function (d) {
                    const cellWidth = d.x1 - d.x0;
                    const cellHeight = d.y1 - d.y0;
                    const cellElement = d3.select(this);

                    const fontSize = calculateFontSize(
                        svg,
                        d.data[0],
                        cellWidth,
                        cellHeight
                    );

                    if (cellWidth > labelWidthThreshold) {
                        // Cell labels
                        cellElement
                            .append("text")
                            .attr("x", cellWidth / 2)
                            .attr("y", cellHeight / 2 - 2)
                            .attr("text-anchor", "middle")
                            .attr("dominant-baseline", "central")
                            .text((d) => formatCellName(d.data[0]))
                            .attr("font-size", fontSize + "px")
                            .call(wrap, cellWidth, cellHeight, 3, fontSize)
                            .attr("class", "cell-text");
                    }
                });

                addTooltips(
                    chart,
                    primaryCountName,
                    secondaryCountName,
                    primaryCountTotal,
                    format,
                    percentageFormat
                );

                if (svgRef) {
                    svgRef.current = svg.node();
                }
            };
            createChart();
        }
    }, [
        data,
        width,
        height,
        marginLeft,
        marginBottom,
        marginTop,
        marginRight,
        widthThreshold,
        primaryCountName,
        secondaryCountName,
        otherRowThreshold,
        otherRowHeightThresHold,
        labelWidthThreshold,
        verticalColumnLabelThreshold,
        rowName,
        columnName,
        primaryCount,
        secondaryCount,
        svgRef,
    ]);

    return <div id={"testidtest"} ref={chartRef}></div>;
};

export default MosaicPlot;

const addTooltips = (
    chart,
    primaryCountName,
    secondaryCountName,
    primaryCountTotal,
    format,
    percentageFormat
) => {
    const allElements = chart.selectAll("rect,text.cell-text");

    allElements.attr("data-tippy-content", (d) => {
        const columnTotal = d.parent.value;
        if (d.depth === 1) {
            return `<b>${d.data[0]}</b>:<br>${primaryCountName}: ${format(
        d.value
      )} (${percentageFormat(
        d.value / primaryCountTotal
      )})<br>${secondaryCountName}: ${format(d.secondaryCount)}`;
        } else if (d.otherItems) {
            return (
                "<div class='max-h-96 overflow-auto'>" +
                d.otherItems
                    .map((item) => {
                        return `<b>${item.rowName}</b> - ${primaryCountName}: ${format(
              item.primaryCount
            )} (${percentageFormat(
              item.primaryCount / columnTotal
            )}), ${secondaryCountName}: ${format(item.secondaryCount)}`;
                    })
                    .join("<br>") +
                "</div>"
            );
        } else {
            return `<b>${d.data[0]}</b>:<br>${primaryCountName}: ${format(
        d.value
      )} (${percentageFormat(
        d.value / columnTotal
      )}) <br>${secondaryCountName}: ${format(d.secondaryCount)}`;
        }
    });
    tippy(allElements.nodes(), {
        allowHTML: true,
        maxWidth: "none",
        interactive: true,
        appendTo: () => document.body,
    });
};

// For smaller cells, use smaller text.
function calculateFontSize(
    svg,
    text,
    width,
    height,
    maxFontSize = 15,
    minFontSize = 9
) {
    const tempText = svg.append("text").text(text);

    let fontSize = maxFontSize;
    tempText.attr("font-size", fontSize + "px");

    while (fontSize > minFontSize) {
        let textWidth = tempText.node().getComputedTextLength();
        let textHeight = fontSize;

        if (textWidth <= width && textHeight <= height) {
            break;
        }

        fontSize--;
        tempText.attr("font-size", fontSize + "px");
    }
    // Otherwise this stays in the DOM
    tempText.remove();
    return fontSize;
}

// Wrap text that is too long. I don't think we can use CSS properties because the chart is made of SVG.
function wrap(text, width, height, padding, fontSize) {
    const actualWidth = width - padding < 0 ? 0 : width - padding;

    text.each(function () {
        var text = d3.select(this),
            words = text.text().split(/\s+/),
            word,
            line = [],
            lineNumber = 0,
            lineHeight = 1.1, // ems
            x = text.attr("x"),
            y = text.attr("y"),
            dy = 0,
            tspan = text
                .text(null)
                .append("tspan")
                .attr("x", x)
                .attr("y", y)
                .attr("dy", dy + "em");

        let wrapCount = 0;

        // Set the font size for accurate text length calculations
        text.attr("font-size", fontSize + "px");

        for (let i = 0; i < words.length; i++) {
            word = words[i];
            line.push(word);
            let newText = line.join(" ");
            const existingText = tspan.text();

            // Preserve wrapped text if wrapping happens. Otherwise the wrapped text gets replaced.
            if (existingText && wrapCount) {
                newText = existingText + " " + newText;
            }
            tspan.text(newText);

            if (tspan.node().getComputedTextLength() > actualWidth) {
                line.pop();
                if (line.length) {
                    tspan.text(line.join(" "));
                    line = [word];
                    tspan = text
                        .append("tspan")
                        .attr("x", x)
                        .attr("y", y)
                        .attr("dy", ++lineNumber * lineHeight + dy + "em")
                        .text(word);
                    wrapCount++;
                } else {
                    // If a single word is longer than the width, break it
                    let brokenWord = "";
                    for (let j = 0; j < word.length; j++) {
                        let testWord = brokenWord + word[j];
                        if (existingText && wrapCount) {
                            testWord = existingText + " " + testWord;
                        }
                        tspan.text(testWord);
                        if (tspan.node().getComputedTextLength() > actualWidth) {
                            if (brokenWord) {
                                tspan.text(brokenWord + "-");
                                line = [];
                                tspan = text
                                    .append("tspan")
                                    .attr("x", x)
                                    .attr("y", y)
                                    .attr("dy", ++lineNumber * lineHeight + dy + "em")
                                    .text(word.slice(j));
                                wrapCount++;
                            } else {
                                let nextText = word[j];
                                if (existingText && wrapCount) {
                                    nextText = existingText + " " + nextText;
                                }
                                tspan.text(nextText);
                                line = [];
                                tspan = text
                                    .append("tspan")
                                    .attr("x", x)
                                    .attr("y", y)
                                    .attr("dy", ++lineNumber * lineHeight + dy + "em")
                                    .text(word.slice(j + 1));
                                wrapCount++;
                            }
                            break;
                        }
                        brokenWord = testWord;
                    }
                }
            }
        }

        // If the text in a cell wraps, then bring it a bit upwards.
        const tspans = text.selectAll("tspan").nodes();
        tspans.forEach((tspan) => {
            const currentY = +tspan.attributes.y.value;
            let newY = currentY - wrapCount * lineHeight;
            tspan.setAttribute("y", newY);
        });
    });
}

// There may be duplicate combinations of column + row. Those need to be combined in order to sort them correctly.
const combineDuplicates = (data) => {
    const combinedMap = new Map();

    for (let item of data) {
        const key = `${item.columnName}-${item.rowName}`;
        if (combinedMap.has(key)) {
            const entry = combinedMap.get(key);
            entry.primaryCount += item.primaryCount;
            entry.secondaryCount += item.secondaryCount;
        } else {
            combinedMap.set(key, { ...item });
        }
    }

    return Array.from(combinedMap.values());
};

const groupByCategory = (data) => {
    return Object.entries(
        data.reduce((acc, item) => {
            if (!acc[item.columnName]) {
                acc[item.columnName] = [];
            }
            acc[item.columnName].push(item);
            return acc;
        }, {})
    ).map(([columnName, items]) => ({ columnName, items }));
};

const processData = (
    data,
    otherRowThreshold,
    otherRowHeightThresHold,
    rowName,
    columnName,
    primaryCount,
    secondaryCount
) => {
    console.log("MosaicPlot - Processing data with raw input:", data?.length, "items");

    const newData = convertKeyNames(
        data,
        rowName,
        columnName,
        primaryCount,
        secondaryCount
    );
    console.log("MosaicPlot - After converting key names:", newData?.length, "items");

    const processedData = groupByCategory(combineDuplicates(newData))
        .sort((a, b) => {
            // Sort in descending order by columnNames.
            const totalCountA = a.items.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );
            const totalCountB = b.items.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );
            return totalCountB - totalCountA;
        })
        .map(({ columnName, items }) => {
            // Get total to calculate threshold.
            const totalCount = items.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );

            const threshold = Math.ceil(totalCount * (1 - otherRowThreshold));

            // Sort the items in descending order.
            const sortedItems = items.sort((a, b) => b.primaryCount - a.primaryCount);

            const processedItems = [];

            for (let i = 0, sum = 0; i < sortedItems.length; i++) {
                const item = sortedItems[i];

                // If the row is too small of a percentage, just turn everything smaller than it into the other row.
                const rowTooSmall =
                    item.primaryCount / totalCount < otherRowHeightThresHold;

                if ((sum > threshold && i !== sortedItems.length - 1) || rowTooSmall) {
                    // Group remaining items into "other"
                    const remainingItems = sortedItems.slice(i).reverse();

                    const otherCount = remainingItems.reduce(
                        (sum, item) => sum + item.primaryCount,
                        0
                    );

                    const otherSecondaryCount = remainingItems.reduce(
                        (sum, item) => sum + item.secondaryCount,
                        0
                    );

                    processedItems.push({
                        columnName: columnName,
                        rowName: "Other", // Other
                        primaryCount: otherCount,
                        secondaryCount: otherSecondaryCount,
                        otherItems: remainingItems,
                    });

                    break;
                } else {
                    processedItems.push(item);
                    sum += item.primaryCount;
                }
            }

            processedItems.reverse();

            return {
                columnName,
                items: processedItems,
            };
        })
        .flatMap(({ items }) => items);

    console.log("MosaicPlot - Final processed data:", processedData?.length, "items");
    return processedData;
};

// There may be duplicate combinations of column + row. Those need to be combined in order to sort them correctly.
const convertKeyNames = (
    data,
    rowName,
    columnName,
    primaryCount,
    secondaryCount
) => {
    const convertedArray = data.map((obj) => ({
        rowName: obj[rowName],
        columnName: obj[columnName],
        primaryCount: obj[primaryCount],
        secondaryCount: obj[secondaryCount],
    }));
    return convertedArray;
};

// Turns "cell_name" into "Cell Name".
const formatCellName = (str) => {
    // Replace underscores with spaces
    str = str.replace(/_/g, " ");

    // Capitalize the first letter of the string
    str = str.charAt(0).toUpperCase() + str.slice(1);

    // Capitalize letters after spaces
    str = str.replace(/\s[a-z]/g, (letter) => letter.toUpperCase());

    return str;
};